import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  ArrowLeft, 
  MapPin, 
  Package, 
  CheckCircle, 
  Camera,
  Upload,
  X,
  FileText,
  AlertCircle,
  MessageCircle,
  CalendarDays,
  Truck,
  Route,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { DriverCoachmark } from '../components/DriverCoachmark';
import { useDriverOnboarding } from '../hooks/useDriverOnboarding';
import { SHIPMENT_DETAIL_PAGE_STEPS_INTERNAL, SHIPMENT_DETAIL_PAGE_STEPS_EXTERNAL } from '../utils/driverOnboardingSteps';
import { shipmentService } from '../services/shipmentService';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { documentService } from '../services/documentService';
import { accountingService } from '../services/accountingService';
import { warehouseService } from '../services/warehouseService';
import { driverActivityService } from '../services/driverActivityService';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { isCorporateManagedExternalDriverUser } from '../utils/userRoles';
import { toast } from 'sonner';

const PROCESS_STAGE_TO_CURRENT_STAGE: Record<string, number> = {
  'atandi-islem-bekliyor': 1,
  'yukleme-bekliyor': 2,
  'yukleme-devam': 3,
  'yukleme-tamamlandi': 4,
  'tasima-yolunda': 5,
  'teslim-noktasina-ulasildi': 6,
  'bosaltma-bekliyor': 7,
  'bosaltma-devam': 8,
  'bosaltma-tamamlandi': 9,
};

const DRIVER_COMPACT_STAGES = [
  { key: 'assignment-pending', label: 'Görev Kabulü Bekleniyor', icon: '🕒', currentStage: 1 },
  { key: 'assignment-accepted', label: 'Görev Kabul Edildi', icon: '✅', currentStage: 1 },
  { key: 'unloading-waiting', label: 'Boşaltma Bekliyor', icon: '⏳', currentStage: 7 },
  { key: 'unloading-completed', label: 'Boşaltma Tamamlandı', icon: '🎉', currentStage: 9 },
] as const;

const mapProcessStatusToCurrentStage = (processStatus?: string, currentStage?: number) => {
  const normalized = String(processStatus || '').trim();
  if (normalized && PROCESS_STAGE_TO_CURRENT_STAGE[normalized]) return PROCESS_STAGE_TO_CURRENT_STAGE[normalized];
  const fallback = Number(currentStage || 1);
  if (Number.isFinite(fallback) && fallback > 0) return fallback;
  return 1;
};

const SHIPMENT_STAGES = [
  { id: 1, key: 'going-to-pickup', label: 'Yük Almaya Gidiyor', icon: '🚛', requiresDocument: false },
  { id: 2, key: 'loading-waiting', label: 'Yükleme Bekliyor', icon: '⏳', requiresDocument: false },
  { id: 3, key: 'loading-started', label: 'Yükleme Başladı', icon: '📦', requiresDocument: false },
  { id: 4, key: 'loading-completed', label: 'Yükleme Tamamlandı', icon: '✅', requiresDocument: true },
  { id: 5, key: 'going-to-delivery', label: 'Teslimat Noktasına Gidiyor', icon: '🚚', requiresDocument: false },
  { id: 6, key: 'arrived-at-delivery', label: 'Teslimat Noktasına Ulaşıldı', icon: '📍', requiresDocument: false },
  { id: 7, key: 'unloading-waiting', label: 'Boşaltma Bekliyor', icon: '⏳', requiresDocument: false },
  { id: 8, key: 'unloading-started', label: 'Boşaltma Başladı', icon: '📤', requiresDocument: false },
  { id: 9, key: 'unloading-completed', label: 'Boşaltma Tamamlandı', icon: '🎉', requiresDocument: true },
];

const UNLOADING_DOCUMENT_OPTIONS = [
  { key: 'delivery-report', label: 'Teslim Evrakı' },
  { key: 'shortage-surplus-report', label: 'Eksik/Fazla Evrak' },
  { key: 'incident-report', label: 'Tutanak' },
] as const;

const MOBILE_DOCUMENT_MAX_SIZE_BYTES = 50 * 1024 * 1024;
const UNIFIED_UNLOADING_DOCUMENT_TYPE = 'delivery-report';
const NO_SPINNER = '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
const MOBILE_IMAGE_TARGET_MAX_BYTES = 2 * 1024 * 1024;

const formatDateValue = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('tr-TR');
};

const buildLocationText = (parts: Array<string | null | undefined>) => {
  const values = parts.map((item) => String(item || '').trim()).filter(Boolean);
  return values.length > 0 ? values.join(' / ') : '-';
};

const resolveDocumentUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  const pathStr = String(filePath);
  if (pathStr.startsWith('http://') || pathStr.startsWith('https://') || pathStr.startsWith('data:') || pathStr.startsWith('blob:')) return pathStr;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${pathStr.startsWith('/') ? pathStr : `/${pathStr}`}`;
};

const mapShipmentDocuments = (documents: any[] | undefined) => {
  return (Array.isArray(documents) ? documents : []).reduce<Record<string, any>>((acc, document) => {
    const documentType = String(document?.documentType || document?.document_type || '').trim();
    const stage = Number(document?.stage || 0);
    let key = '';

    if (['delivery-report', 'shortage-surplus-report', 'incident-report'].includes(documentType)) {
      key = `unloading_${documentType}`;
    } else if (documentType === 'invoice') {
      key = 'invoice';
    } else if (documentType === 'shipment-stage-document' && stage > 0) {
      key = `stage_${stage}`;
    }

    if (!key) return acc;

    const current = acc[key] || { fileNames: [], count: 0, documentType, uploadedAt: document?.uploadedAt || document?.uploaded_at || null };
    return {
      ...acc,
      [key]: {
        ...current,
        fileNames: [...current.fileNames, document?.fileName || document?.file_name || 'Belge'],
        count: Number(current.count || 0) + 1,
        uploadedAt: document?.uploadedAt || document?.uploaded_at || current.uploadedAt || null,
      },
    };
  }, {});
};

const isCompressibleImage = (file: File) => file.type.startsWith('image/') && file.type !== 'image/gif';

const loadImageElement = (file: File) => new Promise<HTMLImageElement>((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error(`Gorsel okunamadi: ${file.name}`));
  };
  image.src = objectUrl;
});

const compressImageFile = async (file: File) => {
  if (!isCompressibleImage(file) || file.size <= MOBILE_IMAGE_TARGET_MAX_BYTES) {
    return file;
  }

  const image = await loadImageElement(file);
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d');
  if (!context) {
    return file;
  }
  context.drawImage(image, 0, 0, image.width, image.height);

  let quality = 0.9;
  let blob: Blob | null = null;
  while (quality >= 0.45) {
    blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= MOBILE_IMAGE_TARGET_MAX_BYTES) {
      break;
    }
    quality -= 0.1;
  }

  if (!blob) {
    return file;
  }

  return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
};

const prepareFilesForMobileUpload = async (files: File[]) => {
  const prepared = await Promise.all(files.map((file) => compressImageFile(file)));
  const oversized = prepared.find((file) => file.size > MOBILE_DOCUMENT_MAX_SIZE_BYTES);
  if (oversized) {
    throw new Error(`Her dosya 50MB'dan küçük olmalıdır. Hatalı dosya: ${oversized.name}`);
  }
  return prepared;
};

export function DriverMobileShipmentTracking() {
  const navigate = useNavigate();
  const { shipmentId } = useParams();
  const { showTour, initialized, advanceTour, skipTour } = useDriverOnboarding('tracking');
  const documentUploadSectionRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const invoiceFileInputRef = useRef<HTMLInputElement>(null);
  const invoiceCameraInputRef = useRef<HTMLInputElement>(null);
  const storedDriverUser = getStoredDriverMobileUser<any>();
  const backPath = storedDriverUser?.role === 'internal-driver' || storedDriverUser?.driverType === 'internal' ? '/driver-mobile-assignments' : '/driver-mobile-home';
  const isInternalDriver = storedDriverUser?.role === 'internal-driver' || storedDriverUser?.driverType === 'internal';

  const openInput = (mode: 'camera' | 'gallery') => {
    try {
      const target = mode === 'camera' ? cameraInputRef.current : fileInputRef.current;
      if (target) {
        target.value = '';
        target.click();
      }
    } catch (error) {
      console.error(`${mode} input failed:`, error);
      toast.error(mode === 'camera' ? 'Kamera açılamadı' : 'Dosya seçici açılamadı');
    }
  };

  const openInvoiceInput = (mode: 'camera' | 'gallery') => {
    try {
      const target = mode === 'camera' ? invoiceCameraInputRef.current : invoiceFileInputRef.current;
      if (target) {
        target.value = '';
        target.click();
      }
    } catch (error) {
      console.error(`invoice ${mode} input failed:`, error);
      toast.error(mode === 'camera' ? 'Kamera acilamadi' : 'Dosya secici acilamadi');
    }
  };

  const [shipment, setShipment] = useState<any>({
    id: shipmentId,
    shipmentNo: 'SEV-2024-1234',
    orderCode: '-',
    planCode: '-',
    pickupLocation: 'İstanbul Depo - Esenyurt',
    deliveryLocation: 'Ankara Merkez Depo',
    pickupName: '-',
    pickupCity: '-',
    pickupDistrict: '-',
    pickupAddress: '-',
    pickupWarehouseName: '-',
    pickupLatitude: null,
    pickupLongitude: null,
    deliveryName: '-',
    deliveryCity: '-',
    deliveryDistrict: '-',
    deliveryAddress: '-',
    deliveryWarehouseName: '-',
    pickupCustomerPhone: '-',
    deliveryCustomerPhone: '-',
    deliveryLatitude: null,
    deliveryLongitude: null,
    loadType: 'Elektronik Eşya',
    weight: '18 Ton',
    price: '12,500 TL',
    distance: '450 km',
    estimatedDuration: '6 saat',
    palletCount: 0,
    volume: 0,
    requestedVehicleType: '-',
    assignedVehiclePlate: '-',
    assignedTrailerPlate: '-',
    assignedVehicleType: '-',
    driverName: '-',
    driverPhone: '-',
    pickupDate: '',
    deliveryDate: '',
    assignedAt: '',
    notes: '-',
    currentStage: 1,
    documents: {},
    route_points: [],
    current_route_stop_order: 1,
    ugramaBedeli: 0,
    beklemeBedeli: 0,
    hammaliyeBedeli: 0,
  });
  const [openSections, setOpenSections] = useState({
    loading: false,
    delivery: false,
    assignment: false,
    documents: false,
  });

  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [pendingCompletionAfterUpload, setPendingCompletionAfterUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [invoicePreviewUrls, setInvoicePreviewUrls] = useState<string[]>([]);
  const [invoiceDecision, setInvoiceDecision] = useState<'issued' | 'not-issued' | null>(null);
  const [showInvoiceDecision, setShowInvoiceDecision] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [sendingUnloadingProblem, setSendingUnloadingProblem] = useState<string | null>(null);
  const [showUnloadingProblemModal, setShowUnloadingProblemModal] = useState(false);
  const [showCompletionSuccessModal, setShowCompletionSuccessModal] = useState(false);
  const [completionSuccessMessage, setCompletionSuccessMessage] = useState('Teslim evraki kaydedildi. Ana sayfaya donebilirsiniz.');
  const [documentViewer, setDocumentViewer] = useState<{ title: string; docs: Array<{ id: string; fileName: string; url: string; isImage: boolean }>; currentIndex: number; bucket: 'loading' | 'delivery' | 'invoice' } | null>(null);
  const [documentViewerLoading, setDocumentViewerLoading] = useState(false);
  const [documentViewerContentLoading, setDocumentViewerContentLoading] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [unloadingProblemReason, setUnloadingProblemReason] = useState('');
  const [updatingStage, setUpdatingStage] = useState(false);

  useEffect(() => {
    const loadShipment = async () => {
      if (!shipmentId) return;

      try {
        const response = await shipmentService.getById(shipmentId);
        const apiShipment = response.data || response;
        const currentStage = mapProcessStatusToCurrentStage(apiShipment.process_status, apiShipment.current_stage);

        setShipment({
          id: apiShipment.id,
          shipmentNo: apiShipment.shipment_no || `SEV-${apiShipment.id}`,
          orderCode: apiShipment.order_code || '-',
          planCode: apiShipment.plan_code || '-',
          pickupLocation: buildLocationText([apiShipment.pickup_name, apiShipment.pickup_city, apiShipment.pickup_district, apiShipment.pickup_address || apiShipment.loading_address]),
          deliveryLocation: buildLocationText([apiShipment.delivery_name, apiShipment.delivery_city, apiShipment.delivery_district, apiShipment.delivery_address]),
          pickupName: apiShipment.pickup_name || '-',
          pickupCity: apiShipment.pickup_city || '-',
          pickupDistrict: apiShipment.pickup_district || '-',
          pickupAddress: apiShipment.pickup_address || apiShipment.loading_address || '-',
          pickupWarehouseName: apiShipment.pickup_warehouse_name || '-',
          pickupCustomerPhone: apiShipment.loading_contact_phone || apiShipment.shipment_contact_phone || '-',
          pickupLatitude: apiShipment.pickup_latitude != null ? Number(apiShipment.pickup_latitude) : null,
          pickupLongitude: apiShipment.pickup_longitude != null ? Number(apiShipment.pickup_longitude) : null,
          deliveryName: apiShipment.delivery_name || '-',
          deliveryCity: apiShipment.delivery_city || '-',
          deliveryDistrict: apiShipment.delivery_district || '-',
          deliveryAddress: apiShipment.delivery_address || '-',
          deliveryWarehouseName: apiShipment.delivery_warehouse_name || '-',
          deliveryCustomerPhone: apiShipment.delivery_contact_phone || apiShipment.shipment_contact_phone || '-',
          deliveryLatitude: apiShipment.delivery_latitude != null ? Number(apiShipment.delivery_latitude) : null,
          deliveryLongitude: apiShipment.delivery_longitude != null ? Number(apiShipment.delivery_longitude) : null,
          loadType: apiShipment.yuk_tipi || apiShipment.load_type || apiShipment.cargo_type || '-',
          weight: `${apiShipment.weight_kg || apiShipment.cargo_weight || 0} Ton`,
          price: `${Number(apiShipment.final_price || apiShipment.base_price || 0).toLocaleString('tr-TR')} TL`,
          distance: `${apiShipment.distance_km || 0} km`,
          estimatedDuration: apiShipment.estimated_duration_hours ? `${apiShipment.estimated_duration_hours} saat` : '-',
          palletCount: Number(apiShipment.pallet_count || 0),
          volume: Number(apiShipment.yuk_hacim || apiShipment.volume_m3 || 0),
          requestedVehicleType: apiShipment.vehicle_spec_type || '-',
          assignedVehiclePlate: apiShipment.assigned_vehicle_plate || apiShipment.cekici_plaka || '-',
          assignedTrailerPlate: apiShipment.assigned_trailer_plate || apiShipment.dorse_plaka || '-',
          assignedVehicleType: apiShipment.assigned_vehicle_type || apiShipment.vehicle_spec_type || '-',
          driverName: apiShipment.driver_name || '-',
          driverPhone: apiShipment.driver_phone || apiShipment.shipment_contact_phone || '-',
          pickupDate: apiShipment.pickup_date || '',
          deliveryDate: apiShipment.delivery_date || '',
          assignedAt: apiShipment.assigned_at || '',
          notes: apiShipment.notes || '-',
          currentStage: Math.min(Math.max(currentStage, 1), SHIPMENT_STAGES.length),
          processStatus: apiShipment.process_status || '',
          accountingStatus: apiShipment.accounting_status || 'none',
          assignmentResponseStatus: apiShipment.assignment_response_status || 'accepted',
          status: apiShipment.status || '',
          documents: mapShipmentDocuments(apiShipment.documents),
          route_points: apiShipment.route_points || [],
          current_route_stop_order: apiShipment.current_route_stop_order || 1,
          latestNote: apiShipment.last_note || '-',
          latestNoteAuthor: apiShipment.last_note_author_name || '-',
          ugramaBedeli: Number(apiShipment.ugrama_bedeli || 0),
          beklemeBedeli: Number(apiShipment.bekleme_bedeli || apiShipment.bekleme_fee || apiShipment.waiting_fee || 0),
          hammaliyeBedeli: Number(apiShipment.hammaliye_bedeli || 0),
        });
      } catch (error) {
        console.error('Failed to load shipment tracking:', error);
      }
    };

    loadShipment();
  }, [shipmentId]);

  const isCompletedShipment = String(shipment.status || '').trim().toLowerCase() === 'delivered' || String(shipment.processStatus || '').trim() === 'bosaltma-tamamlandi';
  const isUnloadingDocumentStage = isCompletedShipment || ['bosaltma-bekliyor', 'bosaltma-devam', 'bosaltma-tamamlandi'].includes(String(shipment.processStatus || '').trim()) || shipment.currentStage >= 7;
  const currentDocumentKey = isUnloadingDocumentStage ? `unloading_${UNIFIED_UNLOADING_DOCUMENT_TYPE}` : `stage_${shipment.currentStage}`;
  const hasUnloadingDocument = UNLOADING_DOCUMENT_OPTIONS.some((option) => Boolean(shipment.documents[`unloading_${option.key}`]));
  const hasCurrentStageDocument = isUnloadingDocumentStage
    ? hasUnloadingDocument
    : Boolean(shipment.documents[`stage_${shipment.currentStage}`]);
  const isSpotDriver = !isInternalDriver;
  const isCorporateManagedSpotDriver = isSpotDriver && isCorporateManagedExternalDriverUser(storedDriverUser);
  const canUploadSpotInvoice = isSpotDriver && !isCorporateManagedSpotDriver;

  const getCompletionFinancials = () => {
    const navlun = Number(String(shipment.price || '0').replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')) || 0;
    const ugrama = Number(shipment.ugramaBedeli || 0);
    const bekleme = Number(shipment.beklemeBedeli || 0);
    const hammaliye = Number(shipment.hammaliyeBedeli || 0);
    return {
      waitingFee: bekleme,
      beklemeFee: bekleme,
      hammaliyeFee: hammaliye,
      genelToplam: navlun + ugrama + bekleme + hammaliye,
    };
  };

  const currentRoutePoint = shipment.route_points?.find(
    (rp: any) => rp.stop_order === shipment.current_route_stop_order
  );

  const completedRoutePoints = shipment.route_points?.filter((rp: any) => rp.is_completed) || [];
  const remainingRoutePoints = shipment.route_points?.filter((rp: any) => !rp.is_completed) || [];
  const loadingRoutePoints = shipment.route_points?.filter((rp: any) => String(rp.operation_type || '').trim() === 'loading') || [];
  const unloadingRoutePoints = shipment.route_points?.filter((rp: any) => String(rp.operation_type || '').trim() === 'unloading') || [];
  const documentSummary = {
    loading: Object.entries(shipment.documents || {})
      .filter(([key]) => key.startsWith('stage_'))
      .reduce((total, [, value]: any) => total + Number(value?.count || 0), 0),
    delivery: UNLOADING_DOCUMENT_OPTIONS.reduce((total, option) => total + Number(shipment.documents[`unloading_${option.key}`]?.count || 0), 0),
    invoice: (shipment.documents?.invoice?.count || 0),
  };

  const refreshShipmentDocuments = async () => {
    try {
      const response: any = await shipmentService.getShipment(String(shipment.id));
      const refreshed = response?.data || response;
      if (Array.isArray(refreshed?.documents)) {
        setShipment((prev: any) => ({ ...prev, documents: mapShipmentDocuments(refreshed.documents) }));
      }
    } catch (error) {
      console.warn('Shipment documents refresh skipped:', error);
    }
  };
  const toggleSection = (key: keyof typeof openSections) => {
    setOpenSections((current) => ({ ...current, [key]: !current[key] }));
  };

  useEffect(() => {
    if (!showDocumentUpload) return;
    const timer = window.setTimeout(() => {
      documentUploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      documentUploadSectionRef.current?.focus();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [showDocumentUpload]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = Array.from(e.target.files || []);
    const files = rawFiles.length > 0 ? await prepareFilesForMobileUpload(rawFiles).catch((error: any) => {
      toast.error(error?.message || 'Dosyalar hazirlanamadi');
      return [] as File[];
    }) : [];
    if (files.length === 0) return;

    console.info('Driver mobile files selected:', files.map((file) => ({
      name: file.name,
      type: file.type,
      sizeBytes: file.size,
      sizeMb: Number((file.size / (1024 * 1024)).toFixed(2)),
    })));

    setSelectedFiles((current) => {
      const merged = [...current];
      files.forEach((file) => {
        const exists = merged.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified);
        if (!exists) merged.push(file);
      });
      return merged;
    });
  };

  const handleInvoiceFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = Array.from(e.target.files || []);
    const files = rawFiles.length > 0 ? await prepareFilesForMobileUpload(rawFiles).catch((error: any) => {
      toast.error(error?.message || 'Dosyalar hazirlanamadi');
      return [] as File[];
    }) : [];
    if (files.length === 0) return;

    setInvoiceFiles((current) => {
      const merged = [...current];
      files.forEach((file) => {
        const exists = merged.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified);
        if (!exists) merged.push(file);
      });
      return merged;
    });
  };

  const openDocumentViewer = async (bucket: 'loading' | 'delivery' | 'invoice') => {
    try {
      setDocumentViewerLoading(true);
      const documentType = bucket === 'loading' ? 'shipment-stage-document' : bucket === 'delivery' ? '' : 'invoice';
      const response: any = await documentService.getDocuments({
        entityType: 'shipment',
        entityId: String(shipment.id),
        ...(documentType ? { documentType } : {}),
      });
      let docs = Array.isArray(response?.data) ? response.data : [];
      if (bucket === 'delivery') {
        docs = docs.filter((doc: any) => ['delivery-report', 'shortage-surplus-report', 'incident-report'].includes(String(doc.document_type || doc.documentType || '').trim()));
      }
      const normalizedDocs = docs.map((doc: any) => {
        const fileName = doc.file_name || doc.fileName || 'Belge';
        const filePath = doc.file_path || doc.filePath || '';
        const lower = String(fileName).toLowerCase();
        return {
          id: String(doc.id || ''),
          fileName,
          url: resolveDocumentUrl(filePath),
          isImage: /\.(png|jpe?g|webp|gif)$/i.test(lower),
        };
      }).filter((doc: any) => doc.url && doc.url !== '#');

      if (normalizedDocs.length === 0) {
        toast.error('Gosterilecek belge bulunamadi');
        return;
      }

      setDocumentViewer({
        title: bucket === 'loading' ? 'Yukleme Belgeleri' : bucket === 'delivery' ? 'Teslim Belgeleri' : 'Fatura Belgeleri',
        docs: normalizedDocs,
        currentIndex: 0,
        bucket,
      });
      setDocumentViewerContentLoading(true);
    } catch (error) {
      console.error('Document viewer load failed:', error);
      toast.error('Belgeler acilamadi');
    } finally {
      setDocumentViewerLoading(false);
    }
  };

  const handleDeleteViewedDocument = async (docId: string) => {
    if (!documentViewer) return;
    if (!window.confirm('Bu belge silinsin mi?')) return;

    try {
      setDeletingDocumentId(docId);
      await documentService.deleteDocument(docId);
      const nextDocs = documentViewer.docs.filter((doc) => doc.id !== docId);
      if (nextDocs.length === 0) {
        setDocumentViewer(null);
      } else {
        setDocumentViewer({
          ...documentViewer,
          docs: nextDocs,
          currentIndex: Math.min(documentViewer.currentIndex, nextDocs.length - 1),
        });
      }

      setShipment((prev: any) => {
        const docs = { ...(prev.documents || {}) };
        if (documentViewer.bucket === 'loading') {
          const current = docs[`stage_${prev.currentStage}`];
          if (current) {
            docs[`stage_${prev.currentStage}`] = {
              ...current,
              count: Math.max(0, Number(current.count || 0) - 1),
            };
          }
        } else if (documentViewer.bucket === 'delivery') {
          UNLOADING_DOCUMENT_OPTIONS.forEach((option) => {
            const key = `unloading_${option.key}`;
            const current = docs[key];
            if (current && current.fileNames?.some((name: string) => name === documentViewer.docs.find((d) => d.id === docId)?.fileName)) {
              docs[key] = {
                ...current,
                count: Math.max(0, Number(current.count || 0) - 1),
              };
            }
          });
        } else if (documentViewer.bucket === 'invoice' && docs.invoice) {
          docs.invoice = {
            ...docs.invoice,
            count: Math.max(0, Number(docs.invoice.count || 0) - 1),
          };
        }
        return { ...prev, documents: docs };
      });

      toast.success('Belge silindi');
    } catch (error) {
      console.error('Delete document failed:', error);
      toast.error('Belge silinemedi');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  useEffect(() => {
    const nextPreviewUrls = selectedFiles
      .filter((file) => file.type.startsWith('image/'))
      .map((file) => URL.createObjectURL(file));
    setPreviewUrls(nextPreviewUrls);
    return () => {
      nextPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  useEffect(() => {
    const nextPreviewUrls = invoiceFiles
      .filter((file) => file.type.startsWith('image/'))
      .map((file) => URL.createObjectURL(file));
    setInvoicePreviewUrls(nextPreviewUrls);
    return () => {
      nextPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [invoiceFiles]);

  const handleUploadDocument = async () => {
    if (selectedFiles.length === 0 || uploadingDocument) return;

    setUploadingDocument(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    try {
      const documentKey = isUnloadingDocumentStage ? `unloading_${UNIFIED_UNLOADING_DOCUMENT_TYPE}` : currentDocumentKey;
      for (const [index, file] of selectedFiles.entries()) {
        setUploadProgress({ current: index + 1, total: selectedFiles.length });
        console.info('Uploading mobile shipment document:', {
          stage: shipment.currentStage,
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          sizeMb: Number((file.size / (1024 * 1024)).toFixed(2)),
          shipmentId: shipment.id,
          documentType: isUnloadingDocumentStage ? UNIFIED_UNLOADING_DOCUMENT_TYPE : 'shipment-stage-document',
        });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'shipment');
        formData.append('entityId', String(shipment.id));
        formData.append('documentType', isUnloadingDocumentStage ? UNIFIED_UNLOADING_DOCUMENT_TYPE : 'shipment-stage-document');
        formData.append('name', file.name);
        formData.append('stage', String(isUnloadingDocumentStage ? 9 : shipment.currentStage));
        await documentService.uploadDocument(formData);
      }

      setShipment((prev: any) => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentKey]: {
            fileNames: selectedFiles.map((file) => file.name),
            uploadedAt: new Date().toISOString(),
            count: selectedFiles.length,
            documentType: isUnloadingDocumentStage ? UNIFIED_UNLOADING_DOCUMENT_TYPE : 'shipment-stage-document',
          }
        }
      }));

      setSelectedFiles([]);
      setPreviewUrls([]);
      await refreshShipmentDocuments();
      toast.success(`${selectedFiles.length} dosya başarıyla yüklendi`);

      if (pendingCompletionAfterUpload && !isSpotDriver && !isCompletedShipment) {
        setShowDocumentUpload(false);
        setPendingCompletionAfterUpload(false);
        await finalizeCompactStage('unloading-completed');
      }
    } catch (error) {
      console.error('Failed to upload tracking document:', error);
      toast.error(error instanceof Error ? error.message : 'Belge yüklenemedi');
    } finally {
      setUploadingDocument(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const resetInvoiceDecisionState = () => {
    setShowInvoiceDecision(false);
    setInvoiceDecision(null);
    setInvoiceFiles([]);
    setInvoicePreviewUrls([]);
  };

  const uploadInvoiceDocuments = async () => {
    if (invoiceFiles.length === 0) {
      throw new Error('Lutfen en az bir fatura evraki ekleyin');
    }

    for (const file of invoiceFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'shipment');
      formData.append('entityId', String(shipment.id));
      formData.append('documentType', 'invoice');
      formData.append('name', file.name);
      formData.append('stage', '9');
      await documentService.uploadDocument(formData);
    }

    setShipment((prev: any) => ({
      ...prev,
      documents: {
        ...prev.documents,
        invoice: {
          fileNames: invoiceFiles.map((file) => file.name),
          uploadedAt: new Date().toISOString(),
          count: invoiceFiles.length,
          documentType: 'invoice',
        },
      },
    }));
    await refreshShipmentDocuments();
  };

  const finalizeCompactStage = async (targetKey: 'unloading-waiting' | 'unloading-completed', spotAccountingStatus?: 'invoice-waiting' | 'invoice-approval-pending') => {
    const target = DRIVER_COMPACT_STAGES.find((item) => item.key === targetKey);
    if (!target) return;

    try {
      setUpdatingStage(true);
      let nextAccountingStatus = shipment.accountingStatus || 'none';
      await shipmentService.update(String(shipment.id), {
        current_stage: target.currentStage,
        status: targetKey === 'unloading-completed' ? 'delivered' : 'in-transit',
        current_latitude: null,
        current_longitude: null,
      });

      if (targetKey === 'unloading-completed') {
        try {
          await accountingService.completeDelivery(shipment.id, getCompletionFinancials());
          nextAccountingStatus = 'delivery-approval-pending';
          if (isSpotDriver) {
            nextAccountingStatus = spotAccountingStatus || 'invoice-waiting';
            await accountingService.setAccountingStatus(shipment.id, nextAccountingStatus);
          }
        } catch (error) {
          console.error('Delivery completion accounting sync failed:', error);
        }
      }

      setShipment((prev: any) => ({
        ...prev,
        currentStage: target.currentStage,
        processStatus: targetKey === 'unloading-completed' ? 'bosaltma-tamamlandi' : 'bosaltma-bekliyor',
        accountingStatus: targetKey === 'unloading-completed' ? nextAccountingStatus : prev.accountingStatus,
        assignmentResponseStatus: 'accepted',
        status: targetKey === 'unloading-completed' ? 'delivered' : 'in-transit',
      }));

      if (targetKey === 'unloading-completed') {
        if (spotAccountingStatus === 'invoice-waiting') {
          setCompletionSuccessMessage('Sevkiyati basariyla tamamladiniz. Fatura daha sonra yuklenecek olarak kaydedildi.');
          setShowCompletionSuccessModal(true);
        } else if (spotAccountingStatus === 'invoice-approval-pending') {
          setCompletionSuccessMessage('Sevkiyati basariyla tamamladiniz. Fatura evraklari da yuklenerek kaydedildi.');
          setShowCompletionSuccessModal(true);
        } else if (isCorporateManagedSpotDriver) {
          setCompletionSuccessMessage('Sevkiyati basariyla tamamladiniz. Fatura yukleme firma hesabindan yapilacaktir.');
          setShowCompletionSuccessModal(true);
        } else {
          setCompletionSuccessMessage('Teslim evraki kaydedildi. Ana sayfaya donebilirsiniz.');
          setShowCompletionSuccessModal(true);
        }
      } else {
        toast.success('Asama guncellendi');
      }
    } catch (error) {
      console.error('Compact stage update failed:', error);
      toast.error('Asama guncellenemedi');
    } finally {
      setUpdatingStage(false);
    }
  };

  const handleSpotInvoiceDecisionComplete = async () => {
    if (invoiceDecision === 'issued' && canUploadSpotInvoice) {
      try {
        setUploadingDocument(true);
        setUploadProgress({ current: 0, total: invoiceFiles.length });
        await uploadInvoiceDocuments();
        resetInvoiceDecisionState();
        await finalizeCompactStage('unloading-completed', 'invoice-approval-pending');
        setSelectedFiles([]);
        setPreviewUrls([]);
        setShowDocumentUpload(false);
      } catch (error: any) {
        console.error('Invoice completion flow failed:', error);
        toast.error(error?.message || 'Fatura evraklari yuklenemedi');
      } finally {
        setUploadingDocument(false);
        setUploadProgress({ current: 0, total: 0 });
      }
      return;
    }

    resetInvoiceDecisionState();
    await finalizeCompactStage('unloading-completed', 'invoice-waiting');
    setSelectedFiles([]);
    setPreviewUrls([]);
    setShowDocumentUpload(false);
  };

  const handleReportUnloadingProblem = async () => {
    const reason = unloadingProblemReason.trim();
    if (!reason) {
      toast.error('Lütfen tahliye sorunu nedenini yazın');
      return;
    }
    try {
      await reportUnloadingProblem(reason);
    } finally {
      setShowUnloadingProblemModal(false);
      setUnloadingProblemReason('');
      setSendingUnloadingProblem(null);
    }
  };

  const handleNextStage = async (skipDocumentCheck = false) => {
    if (updatingStage) return;
    const currentStage = SHIPMENT_STAGES.find(s => s.id === shipment.currentStage);
    
    if (currentStage?.requiresDocument && !skipDocumentCheck) {
      if (!hasCurrentStageDocument) {
        setShowDocumentUpload(true);
        return;
      }
    }

    if (shipment.currentStage < SHIPMENT_STAGES.length) {
      const nextStage = shipment.currentStage + 1;
      try {
        setUpdatingStage(true);
        await shipmentService.update(String(shipment.id), {
          current_stage: nextStage,
          status: nextStage >= SHIPMENT_STAGES.length ? 'delivered' : nextStage <= 6 ? 'assigned' : 'in-transit',
          current_latitude: null,
          current_longitude: null,
        });

        setShipment((prev: any) => ({
          ...prev,
          currentStage: nextStage,
        }));
      } catch (error) {
        console.error('Failed to persist shipment stage:', error);
        alert('Asama backend\'e kaydedilemedi');
      } finally {
        setUpdatingStage(false);
      }
    } else {
      try {
        setUpdatingStage(true);
          if (hasCurrentStageDocument) {
            await accountingService.completeDelivery(shipment.id, getCompletionFinancials());
          }
        await shipmentService.update(String(shipment.id), {
          current_stage: SHIPMENT_STAGES.length,
          status: 'delivered',
        });
      } catch (error) {
        console.error('Failed to complete shipment:', error);
      } finally {
        setUpdatingStage(false);
      }
      alert('🎉 Sevkiyat tamamlandı! muhasebe süreçlerinden sonraki adımları takip edebilirsiniz.');
      navigate(backPath);
    }
  };

  const handlePreviousStage = async () => {
    if (shipment.currentStage <= 1 || updatingStage) return;
    const prevStage = shipment.currentStage - 1;

    try {
      setUpdatingStage(true);
      await shipmentService.update(String(shipment.id), {
        current_stage: prevStage,
        status: prevStage <= 6 ? 'assigned' : 'in-transit',
        current_latitude: null,
        current_longitude: null,
      });

      setShipment((prev: any) => ({
        ...prev,
        currentStage: prevStage,
      }));
    } catch (error) {
      console.error('Failed to revert shipment stage:', error);
      alert('Aşama geri alınamadı');
    } finally {
      setUpdatingStage(false);
    }
  };

  const reportUnloadingProblem = async (reasonLabel: string) => {
    if (!shipment?.id) return;

    try {
      setSendingUnloadingProblem(reasonLabel);

      const driverUserRaw = sessionStorage.getItem('driverMobileUser');
      const driverUser = driverUserRaw ? JSON.parse(driverUserRaw) : null;

      const response: any = await Promise.race([
        warehouseService.createWaitingRecord({
          shipmentId: shipment.id,
          orderNumber: shipment.shipmentNo,
          vehiclePlate: shipment.vehiclePlate || driverUser?.vehiclePlate || '-',
          driverName: driverUser?.full_name || driverUser?.name || '-',
          location: shipment.deliveryLocation || '-',
          locationType: 'Teslimat',
          waitingReason: reasonLabel,
          startTime: new Date().toISOString(),
          endTime: null,
          duration: 0,
          cost: 0,
          status: 'Devam Ediyor',
          notes: `Mobil surucu tahliye problemi bildirdi: ${reasonLabel}`,
          isChargeable: false,
          issueCategory: 'unloading-problem',
          latitude: null,
          longitude: null,
          reportedAt: new Date().toISOString(),
          source: 'driver-mobile',
        }),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Zaman asimi')), 20000)),
      ]);

      if (!response?.success) {
        throw new Error(response?.message || 'Bildirim gonderilemedi');
      }

      alert('Tahliye problemi operasyon ekibine bildirildi. Kaydiniz alindi.');
    } catch (error) {
      console.error('Unloading problem report failed:', error);
      alert('Tahliye problemi bildirilemedi. Lutfen tekrar deneyin.');
    }
  };

  const handleAddShipmentNote = async () => {
    const note = window.prompt(`Sevkiyat notunu girin (#${shipment.shipmentNo})`, shipment.latestNote && shipment.latestNote !== '-' ? shipment.latestNote : '');
    if (!note?.trim()) return;

    try {
      const response: any = await shipmentService.addShipmentNote(String(shipment.id), note.trim());
      if (!response?.success) {
        throw new Error(response?.message || 'Not kaydedilemedi');
      }
      setShipment((prev: any) => ({
        ...prev,
        latestNote: response?.data?.last_note || note.trim(),
        latestNoteAuthor: response?.data?.last_note_author_name || storedDriverUser?.name || storedDriverUser?.full_name || '-',
      }));
      toast.success(response?.message || 'Sevkiyat notu kaydedildi');
    } catch (error: any) {
      console.error('Driver mobile add shipment note failed:', error);
      toast.error(error?.message || 'Sevkiyat notu kaydedilemedi');
    }
  };

  const applyCompactStage = async (targetKey: 'unloading-waiting' | 'unloading-completed') => {
    if (updatingStage) return;
    const target = DRIVER_COMPACT_STAGES.find((item) => item.key === targetKey);
    if (!target) return;

    if (targetKey === 'unloading-completed' && !hasUnloadingDocument) {
      toast.warning('Evrak yüklemeden görevi tamamlayamazsınız. Lütfen önce teslim evrakını yükleyin.');
      if (!isSpotDriver) {
        setPendingCompletionAfterUpload(true);
      }
      setShowDocumentUpload(true);
      return;
    }

    if (targetKey === 'unloading-completed' && isCorporateManagedSpotDriver) {
      await finalizeCompactStage(targetKey, 'invoice-waiting');
      return;
    }

    if (targetKey === 'unloading-completed' && isSpotDriver) {
      setShowInvoiceDecision(true);
      setInvoiceDecision(null);
      return;
    }

    await finalizeCompactStage(targetKey);
  };

  const handleAcceptAssignment = async () => {
    if (!shipment?.id || updatingStage) return;
    try {
      setUpdatingStage(true);
      const response: any = await shipmentServiceReal.respondAssignment(String(shipment.id), { action: 'accepted' });
      setShipment((prev: any) => ({
        ...prev,
        assignmentResponseStatus: 'accepted',
        processStatus: prev.processStatus || 'atandi-islem-bekliyor',
        currentStage: 1,
        status: prev.status || 'assigned',
      }));
      toast.success(response?.message || 'Görev onaylandı');
    } catch (error: any) {
      console.error('Assignment accept failed:', error);
      toast.error(error?.message || 'Görev onaylanamadı');
    } finally {
      setUpdatingStage(false);
    }
  };

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-600 dark:to-blue-700 dark:text-white dark:shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(backPath)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 font-semibold text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Sevkiyat Takibi</h1>
            <p className="text-sm text-slate-500 dark:text-blue-100">{shipment.shipmentNo}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700 dark:bg-white/15 dark:text-white">Plan: {shipment.planCode || '-'}</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700 dark:bg-white/15 dark:text-white">Siparis: {shipment.orderCode || '-'}</span>
          <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-700 dark:bg-white/15 dark:text-white">Durum: {shipment.processStatus || '-'}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Sevkiyat Bilgileri
              </h2>
              <p className="mt-1 text-xs text-gray-500">Sevk siparisi ve teslimat detaylari</p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-2 text-right">
              <div className="text-[11px] text-gray-500">Navlun</div>
              <div className="text-sm font-bold text-emerald-700">{shipment.price}</div>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <button onClick={handleAddShipmentNote} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              <MessageCircle className="w-4 h-4" />
              Not Yaz
            </button>
          </div>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3">
                <div className="text-[11px] text-gray-500">Sevk Kodu</div>
                <div className="mt-1 font-semibold text-gray-900">{shipment.shipmentNo}</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3">
                <div className="text-[11px] text-gray-500">Plan Kodu</div>
                <div className="mt-1 font-semibold text-gray-900">{shipment.planCode || '-'}</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3">
                <div className="text-[11px] text-gray-500">Siparis Kodu</div>
                <div className="mt-1 font-semibold text-gray-900">{shipment.orderCode || '-'}</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3">
                <div className="text-[11px] text-gray-500">Istenen Kasa Tipi</div>
                <div className="mt-1 font-semibold text-gray-900">{shipment.requestedVehicleType || '-'}</div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50">
              <button type="button" onClick={() => toggleSection('loading')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-bold text-emerald-900"><MapPin className="h-4 w-4" /> Yukleme</div>
                {openSections.loading ? <ChevronDown className="h-4 w-4 text-emerald-900" /> : <ChevronRight className="h-4 w-4 text-emerald-900" />}
              </button>
              {openSections.loading ? <div className="space-y-3 border-t border-emerald-200 px-4 py-4">
                <div><span className="text-xs text-emerald-700">Musteri</span><div className="font-semibold text-gray-900">{shipment.pickupName}</div></div>
                <div><span className="text-xs text-emerald-700">Il / Ilce</span><div className="font-medium text-gray-900">{buildLocationText([shipment.pickupCity, shipment.pickupDistrict])}</div></div>
                <div><span className="text-xs text-emerald-700">Adres</span><div className="font-medium text-gray-900">{shipment.pickupAddress}</div></div>
                <div><span className="text-xs text-emerald-700">Depo</span><div className="font-medium text-gray-900">{shipment.pickupWarehouseName}</div></div>
                <div className="flex items-center justify-between gap-3 rounded-lg bg-white/70 px-3 py-2">
                  <div><span className="text-xs text-emerald-700">Musteri Telefonu</span><div className="font-medium text-gray-900">{shipment.pickupCustomerPhone || '-'}</div></div>
                  <div className="flex gap-2">
                    {shipment.pickupCustomerPhone && shipment.pickupCustomerPhone !== '-' ? <a href={`tel:${shipment.pickupCustomerPhone}`} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">Hizli Ara</a> : null}
                    {shipment.pickupLatitude != null && shipment.pickupLongitude != null ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${shipment.pickupLatitude},${shipment.pickupLongitude}`} target="_blank" rel="noreferrer" className="rounded-lg bg-white px-3 py-2 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-300">Yol Tarifi Al</a> : null}
                  </div>
                </div>
                {loadingRoutePoints.length > 0 ? <div><div className="mb-2 text-xs font-semibold uppercase text-emerald-700">Yukleme Ara Noktalari</div><div className="space-y-2">{loadingRoutePoints.map((point: any) => <div key={`loading-${point.id}`} className="rounded-lg bg-white/70 px-3 py-2"><div className="text-sm font-medium text-gray-900">{point.location_name || point.customer_name || '-'}</div><div className="mt-1 text-xs text-gray-600">{buildLocationText([point.city, point.district, point.address])}</div></div>)}</div></div> : null}
              </div> : null}
            </div>

            <div className="overflow-hidden rounded-xl border border-rose-200 bg-rose-50">
              <button type="button" onClick={() => toggleSection('delivery')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-bold text-rose-900"><MapPin className="h-4 w-4" /> Teslimat</div>
                {openSections.delivery ? <ChevronDown className="h-4 w-4 text-rose-900" /> : <ChevronRight className="h-4 w-4 text-rose-900" />}
              </button>
              {openSections.delivery ? <div className="space-y-3 border-t border-rose-200 px-4 py-4">
                <div><span className="text-xs text-rose-700">Musteri</span><div className="font-semibold text-gray-900">{shipment.deliveryName}</div></div>
                <div><span className="text-xs text-rose-700">Il / Ilce</span><div className="font-medium text-gray-900">{buildLocationText([shipment.deliveryCity, shipment.deliveryDistrict])}</div></div>
                <div><span className="text-xs text-rose-700">Adres</span><div className="font-medium text-gray-900">{shipment.deliveryAddress}</div></div>
                <div><span className="text-xs text-rose-700">Depo</span><div className="font-medium text-gray-900">{shipment.deliveryWarehouseName}</div></div>
                <div className="flex items-center justify-between gap-3 rounded-lg bg-white/70 px-3 py-2">
                  <div><span className="text-xs text-rose-700">Musteri Telefonu</span><div className="font-medium text-gray-900">{shipment.deliveryCustomerPhone || '-'}</div></div>
                  <div className="flex gap-2">
                    {shipment.deliveryCustomerPhone && shipment.deliveryCustomerPhone !== '-' ? <a href={`tel:${shipment.deliveryCustomerPhone}`} className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white">Hizli Ara</a> : null}
                    {shipment.deliveryLatitude != null && shipment.deliveryLongitude != null ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${shipment.deliveryLatitude},${shipment.deliveryLongitude}`} target="_blank" rel="noreferrer" className="rounded-lg bg-white px-3 py-2 text-xs font-semibold text-rose-700 ring-1 ring-rose-300">Yol Tarifi Al</a> : null}
                  </div>
                </div>
                {unloadingRoutePoints.length > 0 ? <div><div className="mb-2 text-xs font-semibold uppercase text-rose-700">Bosaltma Ara Noktalari</div><div className="space-y-2">{unloadingRoutePoints.map((point: any) => <div key={`unloading-${point.id}`} className="rounded-lg bg-white/70 px-3 py-2"><div className="text-sm font-medium text-gray-900">{point.location_name || point.customer_name || '-'}</div><div className="mt-1 text-xs text-gray-600">{buildLocationText([point.city, point.district, point.address])}</div></div>)}</div></div> : null}
              </div> : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3"><div className="text-[11px] text-gray-500">Yuk Tipi</div><div className="mt-1 font-medium text-gray-900">{shipment.loadType}</div></div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3"><div className="text-[11px] text-gray-500">Agirlik</div><div className="mt-1 font-medium text-gray-900">{shipment.weight}</div></div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3"><div className="text-[11px] text-gray-500">Hacim</div><div className="mt-1 font-medium text-gray-900">{shipment.volume != null && shipment.volume !== '' ? `${Number(shipment.volume).toLocaleString('tr-TR')} m3` : '-'}</div></div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3"><div className="text-[11px] text-gray-500">Palet</div><div className="mt-1 font-medium text-gray-900">{shipment.palletCount || 0}</div></div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3"><div className="text-[11px] text-gray-500">Mesafe</div><div className="mt-1 font-medium text-gray-900">{shipment.distance}</div></div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-3"><div className="text-[11px] text-gray-500">Tahmini Sure</div><div className="mt-1 font-medium text-gray-900">{shipment.estimatedDuration}</div></div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <button type="button" onClick={() => toggleSection('assignment')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-900"><Truck className="h-4 w-4 text-blue-600" /> Atama</div>
                {openSections.assignment ? <ChevronDown className="h-4 w-4 text-gray-700" /> : <ChevronRight className="h-4 w-4 text-gray-700" />}
              </button>
              {openSections.assignment ? <div className="space-y-4 border-t border-gray-200 px-4 py-4">
                <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[11px] text-gray-500">Surucu</div><div className="mt-1 font-medium text-gray-900">{shipment.driverName || '-'}</div></div>
                <div><div className="text-[11px] text-gray-500">Telefon</div><div className="mt-1 font-medium text-gray-900">{shipment.driverPhone || '-'}</div></div>
                <div><div className="text-[11px] text-gray-500">Cekici</div><div className="mt-1 font-medium text-gray-900">{shipment.assignedVehiclePlate || '-'}</div></div>
                <div><div className="text-[11px] text-gray-500">Dorse</div><div className="mt-1 font-medium text-gray-900">{shipment.assignedTrailerPlate || '-'}</div></div>
                <div><div className="text-[11px] text-gray-500">Kasa Tipi</div><div className="mt-1 font-medium text-gray-900">{shipment.assignedVehicleType || '-'}</div></div>
                <div><div className="text-[11px] text-gray-500">Atama Tarihi</div><div className="mt-1 font-medium text-gray-900">{formatDateValue(shipment.assignedAt)}</div></div>
              </div>
                <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-3">
                <div><div className="text-[11px] text-gray-500">Yukleme Tarihi</div><div className="mt-1 font-medium text-gray-900">{formatDateValue(shipment.pickupDate)}</div></div>
                <div><div className="text-[11px] text-gray-500">Teslim Tarihi</div><div className="mt-1 font-medium text-gray-900">{formatDateValue(shipment.deliveryDate)}</div></div>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-3">
                  <p className="text-gray-500 text-xs">Operasyon Notu</p>
                  <p className="mt-1 font-medium text-gray-900 break-words">{shipment.notes || '-'}</p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-3">
                  <p className="text-gray-500 text-xs">Son Sevkiyat Notu</p>
                  <p className="mt-1 font-medium text-gray-900 break-words">{shipment.latestNote || '-'}</p>
                  <p className="mt-1 text-xs text-gray-500">{shipment.latestNoteAuthor && shipment.latestNoteAuthor !== '-' ? `Yazan: ${shipment.latestNoteAuthor}` : ''}</p>
                </div>
              </div> : null}
            </div>

            <div data-tour="tracking-documents" className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <button type="button" onClick={() => toggleSection('documents')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-900"><FileText className="h-4 w-4 text-blue-600" /> Belgeler</div>
                {openSections.documents ? <ChevronDown className="h-4 w-4 text-gray-700" /> : <ChevronRight className="h-4 w-4 text-gray-700" />}
              </button>
              {openSections.documents ? <div className="space-y-3 border-t border-gray-200 px-4 py-4">
                <div className="grid grid-cols-3 gap-3">
                  <button type="button" onClick={() => void openDocumentViewer('loading')} className="rounded-xl bg-slate-50 p-3 text-center transition hover:bg-slate-100"><div className="text-[11px] text-gray-500">Yukleme</div><div className="mt-1 text-lg font-bold text-gray-900">{documentSummary.loading}</div><div className="mt-1 text-[10px] font-semibold text-blue-600">Belgeleri gormek icin tiklayin</div></button>
                  <button type="button" onClick={() => void openDocumentViewer('delivery')} className="rounded-xl bg-slate-50 p-3 text-center transition hover:bg-slate-100"><div className="text-[11px] text-gray-500">Teslim</div><div className="mt-1 text-lg font-bold text-gray-900">{documentSummary.delivery}</div><div className="mt-1 text-[10px] font-semibold text-blue-600">Belgeleri gormek icin tiklayin</div></button>
                  <button type="button" onClick={() => void openDocumentViewer('invoice')} className="rounded-xl bg-slate-50 p-3 text-center transition hover:bg-slate-100"><div className="text-[11px] text-gray-500">Fatura</div><div className="mt-1 text-lg font-bold text-gray-900">{documentSummary.invoice}</div><div className="mt-1 text-[10px] font-semibold text-blue-600">Belgeleri gormek icin tiklayin</div></button>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-3 text-xs text-slate-600">Teslim evraki, tutanak{canUploadSpotInvoice ? ' ve fatura evraklarini' : ''} asagidaki islem panelinden yukleyebilir veya guncelleyebilirsiniz.</div>
              </div> : null}
            </div>

            {isSpotDriver ? (
              <div data-tour="tracking-extra-expenses" className="mt-3 rounded-2xl border-2 border-violet-300 bg-violet-50 p-4 shadow-sm">
                <div className="mb-3">
                  <div className="text-sm font-extrabold uppercase tracking-wide text-violet-800">Ek Masraflar</div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 shadow-sm space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-amber-700">Bekleme Bedeli</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={shipment.beklemeBedeli ?? 0}
                    readOnly
                    className={`w-full rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-base font-bold text-amber-900 shadow-inner outline-none ${NO_SPINNER}`}
                  />
                </label>
                <label className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 shadow-sm space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-emerald-700">Hammaliye Bedeli</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={shipment.hammaliyeBedeli ?? 0}
                    readOnly
                    className={`w-full rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-base font-bold text-emerald-900 shadow-inner outline-none ${NO_SPINNER}`}
                  />
                </label>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div data-tour="tracking-action-panel" className="rounded-xl border border-black bg-sky-100 p-5 text-black shadow-lg dark:border-slate-700 dark:bg-gradient-to-br dark:from-blue-600 dark:to-blue-700 dark:text-white">
          <div className="mb-4 rounded-lg border-2 border-orange-400 bg-orange-100 p-3 shadow-sm">
            <p className="text-xs font-semibold text-orange-700">Mevcut Durum</p>
            <p className="mt-1 text-sm font-bold text-black">
              {shipment.assignmentResponseStatus === 'pending'
                ? 'Sevkiyat Onayı Bekleniyor'
                : isCompletedShipment
                  ? 'Görev Tamamlandı'
                  : 'Görev Başladı'}
            </p>
            <p className="mt-1 text-xs text-slate-700">
              {isCompletedShipment ? 'Teslim evraklarını aşağıdan yükleyebilir veya düzenleyebilirsiniz.' : 'İşlemi tamamlamak için aşağıdaki aksiyonları sırayla kullanın.'}
            </p>
          </div>

          {isCompletedShipment ? (
            <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-100 px-4 py-3 text-sm font-semibold text-emerald-800">
              ✓ Teslim evrakı kaydedildi ve sevkiyat başarıyla tamamlandı.
            </div>
          ) : hasUnloadingDocument ? (
            <div className="mb-3 rounded-lg border border-blue-300 bg-blue-100 px-4 py-3 text-sm font-semibold text-blue-800">
              ✓ Teslim evrakı yüklendi. Şimdi <strong>Görevi Bitir</strong> ile sevkiyatı tamamlayabilirsiniz.
            </div>
          ) : null}

          {shipment.assignmentResponseStatus === 'pending' ? (
            <button
              onClick={() => void handleAcceptAssignment()}
              disabled={updatingStage}
              className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-black bg-slate-100 py-3 font-bold text-black transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CheckCircle className="w-5 h-5" />
              {updatingStage ? 'Onaylanıyor...' : 'Sevki Onayla'}
            </button>
          ) : null}

          <button
            data-tour="tracking-unload-report"
            type="button"
            onClick={() => setShowUnloadingProblemModal(true)}
            disabled={sendingUnloadingProblem !== null || shipment.assignmentResponseStatus === 'pending'}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-black bg-slate-100 py-3 font-bold text-black transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <AlertCircle className="w-5 h-5" />
            {sendingUnloadingProblem ? 'Bildiriliyor...' : 'Tahliye Sorunu / Durumu Bildir'}
          </button>

      <button
        data-tour="tracking-complete"
        type="button"
        onClick={() => {
          if (!isCompletedShipment && !isSpotDriver) {
            setPendingCompletionAfterUpload(true);
          }
          setShowDocumentUpload(true);
        }}
        disabled={updatingStage || shipment.assignmentResponseStatus === 'pending'}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border py-3 font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${isCompletedShipment ? 'border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700' : hasUnloadingDocument ? 'border-blue-700 bg-blue-600 text-white hover:bg-blue-700' : 'border-black bg-slate-100 text-black hover:bg-slate-200'}`}
      >
            <Upload className="w-5 h-5" />
            {isCompletedShipment ? 'Sevkiyat Tamamlandı • Evrakı Gör / Düzenle' : hasUnloadingDocument ? 'Teslim Evrakı Yüklendi • Görevi Bitir' : 'Görevi Bitir'}
          </button>

          {shipment.assignmentResponseStatus === 'pending' ? (
            <div className="mt-3 rounded-lg border border-black/20 bg-slate-100 p-3 text-xs font-medium text-black">
              Önce sevki onaylayın, ardından tahliye bildirimi veya evrak yükleme ile görevi tamamlayın.
            </div>
          ) : null}

          {showDocumentUpload ? (
            <div ref={documentUploadSectionRef} tabIndex={-1} className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm outline-none">
              <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-200 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Belge Yükle</h3>
                  <p className="mt-1 text-sm text-gray-600">Teslim evrakı, tutanak ve {canUploadSpotInvoice ? 'fatura dahil ' : ''}boşaltma belgelerini çoklu seçerek yükleyin</p>
                </div>
                <button
                  onClick={() => {
                    setShowDocumentUpload(false);
                    setSelectedFiles([]);
                    setPreviewUrls([]);
                    resetInvoiceDecisionState();
                  }}
                  className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {hasUnloadingDocument && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800">
                  <span className="font-semibold">✓ Teslim evrakı yüklendi.</span> Yeni belge eklemek veya mevcut belgeleri değiştirmek için aşağıdan dosya seçip yükleyin.
                </div>
              )}
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800">
                Yüklenen tüm dosyalar admin panelde boşaltma / teslim evrakları altında gösterilir.
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={invoiceFileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleInvoiceFileSelect}
                className="hidden"
              />
              <input
                ref={invoiceCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleInvoiceFileSelect}
                className="hidden"
              />

              {selectedFiles.length === 0 ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => openInput('camera')}
                    className="w-full rounded-xl border-2 border-dashed border-blue-300 p-5 text-left transition-colors hover:border-blue-500 hover:bg-blue-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                        <Camera className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Kamera ile Çek</p>
                        <p className="mt-1 text-sm text-gray-500">Telefon kamerasını açar</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => openInput('gallery')}
                    className="w-full rounded-xl border-2 border-dashed border-gray-300 p-5 text-left transition-colors hover:border-blue-500 hover:bg-blue-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <Upload className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Galeriden Seç</p>
                        <p className="mt-1 text-sm text-gray-500">Çoklu fotoğraf veya PDF seçin</p>
                      </div>
                    </div>
                  </button>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                    Dosya başı maksimum 50MB. Android'de çoklu çekim için kamera ile birer birer ekleyebilir, galeriden çoklu seçim yapabilirsiniz.
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {previewUrls.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {previewUrls.map((previewUrl, index) => (
                        <div key={previewUrl} className="overflow-hidden rounded-lg border border-gray-200">
                          <img src={previewUrl} alt={`Preview ${index + 1}`} className="h-32 w-full object-cover bg-gray-50" />
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-900">{selectedFiles.length} dosya seçildi</p>
                    {uploadingDocument && uploadProgress.total > 0 ? <p className="mt-1 text-xs font-medium text-blue-600">Yükleniyor: {uploadProgress.current}/{uploadProgress.total}</p> : null}
                    <div className="mt-1 space-y-1 text-xs text-gray-500">
                      {selectedFiles.map((file) => (
                        <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-2">
                          <span className="truncate">{file.name}</span>
                          <span>{(file.size / (1024 * 1024)).toFixed(2)} MB • {file.type || 'bilinmiyor'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <button
                      type="button"
                      onClick={() => openInput('gallery')}
                      disabled={uploadingDocument || updatingStage}
                      className="rounded-lg bg-gray-100 px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      Dosya Ekle
                    </button>
                    <button
                      type="button"
                      onClick={() => openInput('camera')}
                      disabled={uploadingDocument || updatingStage}
                      className="rounded-lg bg-blue-50 px-4 py-3 font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      Kamera Ekle
                    </button>
                    <button
                      onClick={handleUploadDocument}
                      disabled={uploadingDocument || updatingStage}
                      className="flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 font-bold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {uploadingDocument ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          {uploadProgress.total > 0 ? `Yükleniyor ${uploadProgress.current}/${uploadProgress.total}` : 'Yükleniyor...'}
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          {!isSpotDriver && !isCompletedShipment ? 'Yükle ve Görevi Tamamla' : 'Yükle'}
                        </>
                        )}
                    </button>
                  </div>
                </div>
              )}

              {!isCompletedShipment ? (
                <button
                  type="button"
                  onClick={() => void applyCompactStage('unloading-completed')}
                   disabled={updatingStage || uploadingDocument}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 font-bold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  <CheckCircle className="w-5 h-5" />
                  {updatingStage ? 'Bitiriliyor...' : 'Görevi Bitir'}
                </button>
              ) : null}

              {!isCompletedShipment && isSpotDriver && !hasUnloadingDocument ? (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  Görevi bitirmek için önce en az bir teslim evrakı yükleyin.
                </div>
              ) : null}

              {showInvoiceDecision && canUploadSpotInvoice && !isCompletedShipment ? (
                <div className="mt-4 rounded-2xl border border-violet-200 bg-violet-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-base font-bold text-violet-900">Bu sevkiyatin faturasi kesildi mi?</h4>
                      <p className="mt-1 text-sm text-violet-800">
                        Evet derseniz fatura evraklarini kamera ile cekebilir veya galeriden birden fazla evrak secerek yukleyebilirsiniz.
                      </p>
                    </div>
                    <button type="button" onClick={resetInvoiceDecisionState} className="rounded-lg p-2 text-violet-700 hover:bg-violet-100">
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button type="button" onClick={() => setInvoiceDecision('issued')} className={`rounded-lg px-4 py-2 font-medium ${invoiceDecision === 'issued' ? 'bg-violet-700 text-white' : 'bg-white text-violet-800 border border-violet-300'}`}>
                      Evet, fatura kesildi
                    </button>
                    <button type="button" onClick={() => setInvoiceDecision('not-issued')} className={`rounded-lg px-4 py-2 font-medium ${invoiceDecision === 'not-issued' ? 'bg-amber-600 text-white' : 'bg-white text-amber-800 border border-amber-300'}`}>
                      Hayir, henuz kesilmedi
                    </button>
                  </div>

                  {invoiceDecision === 'issued' ? (
                    <div className="mt-4 space-y-4">
                      <div className="rounded-lg border border-violet-200 bg-white px-4 py-3 text-xs text-violet-800">
                        Fatura evraklari finansal islemlerde fatura evraklarina yuklenecek ve bu siparis faturalandirildi asamasina alinacaktir.
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <button type="button" onClick={() => openInvoiceInput('camera')} className="rounded-xl border-2 border-dashed border-violet-300 bg-white p-4 text-left hover:border-violet-500 hover:bg-violet-100/40">
                          <div className="font-medium text-gray-900">Evrak Kamera Cek</div>
                          <div className="mt-1 text-sm text-gray-500">Telefon kamerasini acar</div>
                        </button>
                        <button type="button" onClick={() => openInvoiceInput('gallery')} className="rounded-xl border-2 border-dashed border-violet-300 bg-white p-4 text-left hover:border-violet-500 hover:bg-violet-100/40">
                          <div className="font-medium text-gray-900">Evrak Yukle</div>
                          <div className="mt-1 text-sm text-gray-500">Birden fazla fotograf veya PDF secin</div>
                        </button>
                      </div>

                      {invoicePreviewUrls.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                          {invoicePreviewUrls.map((previewUrl, index) => (
                            <div key={previewUrl} className="overflow-hidden rounded-lg border border-violet-200">
                              <img src={previewUrl} alt={`Invoice Preview ${index + 1}`} className="h-32 w-full object-cover bg-gray-50" />
                            </div>
                          ))}
                        </div>
                      ) : null}

                      <div className="rounded-lg bg-white p-3 text-xs text-gray-600">
                        <div className="font-medium text-gray-900">{invoiceFiles.length} fatura evraki secildi</div>
                        <div className="mt-2 space-y-1">
                          {invoiceFiles.map((file) => (
                            <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-2">
                              <span className="truncate">{file.name}</span>
                              <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button type="button" onClick={() => setInvoiceFiles([])} className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50">Secimleri Temizle</button>
                        <button type="button" onClick={() => void handleSpotInvoiceDecisionComplete()} disabled={uploadingDocument || updatingStage || invoiceFiles.length === 0} className="rounded-lg bg-violet-700 px-4 py-2 text-sm font-bold text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-gray-300">
                          Fatura Evraklarini Yukle ve Gorevi Bitir
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {invoiceDecision === 'not-issued' ? (
                    <div className="mt-4 space-y-4">
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Faturayi muhasebe ekranindan yuklemeyi unutmayiniz. Fatura yuklendikten sonra odeme surecine gecilecektir.
                      </div>
                      <button type="button" onClick={() => void handleSpotInvoiceDecisionComplete()} disabled={uploadingDocument || updatingStage} className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-gray-300">
                        Fatura Bekliyor Olarak Bitir
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {shipment.route_points && shipment.route_points.length > 1 && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Route className="w-5 h-5 text-amber-600" />
              Rota Mini Timeline ({shipment.route_points.length})
            </h2>

            {currentRoutePoint && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Sıradaki Durak</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                    currentRoutePoint.operation_type === 'loading'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {currentRoutePoint.operation_type === 'loading' ? 'Yükleme' : 'Boşaltma'}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{currentRoutePoint.stop_order}. Durak</span>
                </div>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {currentRoutePoint.location_name || currentRoutePoint.customer_name || '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {[currentRoutePoint.city, currentRoutePoint.district, currentRoutePoint.address].filter(Boolean).join(' / ') || '—'}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {shipment.route_points.map((point: any, index: number) => {
                const isCompleted = !!point.is_completed;
                const isCurrent = point.stop_order === shipment.current_route_stop_order;
                return (
                  <div
                    key={point.id}
                    className={`flex items-start gap-3 rounded-xl border p-3 ${
                      isCurrent
                        ? 'border-amber-300 bg-amber-50'
                        : isCompleted
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-300 text-gray-600'
                      }`}>
                        {isCompleted ? '✓' : point.stop_order}
                      </div>
                      {index < shipment.route_points.length - 1 ? <div className="mt-1 h-8 w-0.5 bg-slate-300" /> : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          point.operation_type === 'loading'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {point.operation_type === 'loading' ? 'Yükleme' : 'Boşaltma'}
                        </span>
                        {isCurrent && <span className="text-xs text-amber-600 font-medium">← Burada</span>}
                      </div>
                      <p className="text-sm font-medium text-gray-800 mt-0.5">
                        {point.location_name || point.customer_name || '—'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {buildLocationText([point.city, point.district, point.address])}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-xs text-gray-500">
              <span>Tamamlanan: {completedRoutePoints.length}</span>
              <span>Kalan: {remainingRoutePoints.length}</span>
            </div>
          </div>
        )}

      </div>

      {updatingStage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-6">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Güncelleniyor</h3>
            <p className="mt-2 text-sm text-gray-600">Aşama bilgisi kaydediliyor. Lütfen bekleyin.</p>
          </div>
        </div>
      )}

      {showUnloadingProblemModal && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Tahliye Durumu bildir</h3>
                <p className="mt-1 text-sm text-gray-500">Operasyon ekibine gönderilecek açıklamayı yazın</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (sendingUnloadingProblem) return;
                  setShowUnloadingProblemModal(false);
                  setUnloadingProblemReason('');
                }}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Sorun Nedeni</label>
                <textarea
                  value={unloadingProblemReason}
                  onChange={(event) => setUnloadingProblemReason(event.target.value)}
                  rows={5}
                  placeholder="Örn: Rampada yoğunluk var, evrak bekleniyor, yetkili bulunamadı..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  if (sendingUnloadingProblem) return;
                  setShowUnloadingProblemModal(false);
                  setUnloadingProblemReason('');
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={() => void handleReportUnloadingProblem()}
                disabled={Boolean(sendingUnloadingProblem)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sendingUnloadingProblem ? 'Gönderiliyor...' : 'Bildirimi Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompletionSuccessModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle className="h-8 w-8" />
            </div>
            <h3 className="mt-4 text-center text-xl font-bold text-gray-900">Sevkiyatı başarıyla tamamladınız</h3>
            <p className="mt-2 text-center text-sm text-gray-600">{completionSuccessMessage}</p>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  setShowCompletionSuccessModal(false);
                  navigate('/driver-mobile-home');
                }}
                className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Ana Sayfaya Dön
              </button>
            </div>
          </div>
        </div>
      )}

      {documentViewer && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 px-4" onClick={() => setDocumentViewer(null)}>
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{documentViewer.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{documentViewer.currentIndex + 1} / {documentViewer.docs.length}</p>
              </div>
              <button type="button" onClick={() => setDocumentViewer(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-[260px_minmax(0,1fr)]">
              <div className="overflow-auto rounded-xl border border-gray-200 bg-slate-50 p-2">
                <div className="space-y-2">
                  {documentViewer.docs.map((doc, index) => (
                    <div key={`${doc.id}-${index}`} className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${documentViewer.currentIndex === index ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-700'}`}>
                      <button type="button" onClick={() => { setDocumentViewerContentLoading(true); setDocumentViewer((prev) => prev ? { ...prev, currentIndex: index } : null); }} className="w-full text-left">
                        <div className="truncate font-medium">{doc.fileName}</div>
                      </button>
                      <div className="mt-2 flex justify-end">
                        <button type="button" onClick={() => void handleDeleteViewedDocument(doc.id)} disabled={deletingDocumentId === doc.id} className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                          {deletingDocumentId === doc.id ? 'Siliniyor...' : 'Sil'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative min-h-[50vh] overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
                {documentViewerContentLoading ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                    <div className="text-center">
                      <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                      <div className="text-sm font-medium text-slate-700">Belge yukleniyor...</div>
                    </div>
                  </div>
                ) : null}
                {documentViewer.docs[documentViewer.currentIndex]?.isImage ? (
                  <img
                    src={documentViewer.docs[documentViewer.currentIndex].url}
                    alt={documentViewer.docs[documentViewer.currentIndex].fileName}
                    className="h-full w-full object-contain bg-white"
                    onLoad={() => setDocumentViewerContentLoading(false)}
                    onError={() => setDocumentViewerContentLoading(false)}
                  />
                ) : (
                  <iframe
                    src={documentViewer.docs[documentViewer.currentIndex].url}
                    title={documentViewer.docs[documentViewer.currentIndex].fileName}
                    className="h-full min-h-[50vh] w-full bg-white"
                    onLoad={() => setDocumentViewerContentLoading(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {documentViewerLoading && !documentViewer ? (
        <div className="fixed inset-0 z-[84] flex items-center justify-center bg-black/60 px-6">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Belgeler Hazirlaniyor</h3>
            <p className="mt-2 text-sm text-gray-600">Dosyalar yukleniyor, lutfen bekleyin.</p>
          </div>
        </div>
      ) : null}

      {initialized && showTour && (
        <DriverCoachmark
          steps={isInternalDriver ? SHIPMENT_DETAIL_PAGE_STEPS_INTERNAL : SHIPMENT_DETAIL_PAGE_STEPS_EXTERNAL}
          onFinish={() => { advanceTour(null); }}
          onSkip={skipTour}
        />
      )}

    </div>
  );
}
